import React, { useState, useEffect, useMemo } from 'react';
import {
  Grid,
  TextField,
  Divider,
  Button,
  Alert,
  Chip,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import UserSearchModal from '../../../Components/UserSearchModal';
import ChapterSelector from '../../../Components/ChapterSelector';
import { getUserPersonalInfo } from '../../../endpoints/usersTs';
import { getSPDChaptersList } from '../../../endpoints/chaptersTs';
import { useDonationAlreadyPaidHandler } from './useDonationAlreadyPaidHandler';
import StateSelector from '../../../Components/StateSelector';
import CountrySelector from '../../../Components/CountrySelector';
import ValidatedTextField from '../../../Components/ValidatedTextField';
import { emailValidator } from '../../../utils/validators';
import { trimField } from '../../../utils/strings';
import {
  createDonation,
  getChapterDonorProfile,
} from '../../../endpoints/donations';
import { getPostalInfo } from '../../../endpoints/lookups';
import { useApiSend, useApiGet } from '../../../utils/httpClient';
import DonationsChapterSelector from '../../../Components/DonationChapters';

/** Primary physical address type (same as storefront / chapter supplies mail-in). */
const PRIMARY_SHIPPING_ADDRESS_TYPE_ID = 'd5107f490a3049d5969ccbb5f843aac7';

export default function DonationPI({
  handleNext,
  setDonationId,
  refetch,
  donationObject,
  donationId,
  isPaid = false,
  onAlreadyPaid,
  saveDonation = createDonation,
  /** Public donations require email for confirmation; internal wizard passes emailOptional. */
  emailOptional = false,
  allowMemberLink = false,
  /** Internal copy corrections: save donor contact when paid; hide donor link controls. */
  correctionsMode = false,
  /** Internal staff: allow organization donor (enterprise_name) instead of first/last. */
  allowOrganizationDonor = false,
  /** Internal staff: mailing address fields are optional (email-only donations). */
  addressOptional = false,
  /** Internal staff: Special Recognition (is_legacy_chapter) checkbox after a chapter is selected. */
  allowLegacyChapter = false,
  /** donations:legacy without manage: checkbox stays on. */
  legacyLocked = false,
}) {
  const [firstName, setFirstName] = useState('');
  const [firstNameError, setFirstNameError] = useState(false);
  const [lastName, setLastName] = useState('');
  const [lastNameError, setLastNameError] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [chapter, setChapter] = useState('');
  const [countryId, setCountryId] = useState('');
  const [countryIdError, setCountryIdError] = useState(false);
  const [stateRequired, setStateRequired] = useState(false);
  const [stateId, setStateId] = useState('');
  const [stateIdError, setStateIdError] = useState(false);
  const [city, setCity] = useState('');
  const [cityError, setCityError] = useState(false);
  const [address, setAddress] = useState('');
  const [addressError, setAddressError] = useState(false);
  const [postalCode, setPostalCode] = useState('');
  const [postalCodeError, setPostalCodeError] = useState(false);
  const [address2, setAddress2] = useState('');
  const [delayPostalCode, setDelayPostalCode] = useState('');
  const [getPostalInfoActive, setGetPostalInfoActive] = useState(false);
  const [loadedFromDonation, setLoadedFromDonation] = useState(false);
  const [localChapter, setLocalChapter] = useState('');
  const [userSearchModalOpen, setUserSearchModalOpen] = useState(false);
  const [selectedDonorId, setSelectedDonorId] = useState(null);
  const [linkedMemberLabel, setLinkedMemberLabel] = useState('');
  const [selectedLocalChapterDonorId, setSelectedLocalChapterDonorId] =
    useState(null);
  const [linkedLocalChapterLabel, setLinkedLocalChapterLabel] = useState('');
  const [selectedSpdChapterDonorId, setSelectedSpdChapterDonorId] =
    useState(null);
  const [linkedSpdChapterLabel, setLinkedSpdChapterLabel] = useState('');
  const [localChapterDialogOpen, setLocalChapterDialogOpen] = useState(false);
  const [spdChapterDialogOpen, setSpdChapterDialogOpen] = useState(false);
  const [pendingLocalChapter, setPendingLocalChapter] = useState(null);
  const [pendingSpdChapter, setPendingSpdChapter] = useState(null);
  const [chapterLinkLoading, setChapterLinkLoading] = useState(false);
  const [chapterLinkError, setChapterLinkError] = useState('');
  const [irsStatementExcluded, setIrsStatementExcluded] = useState(false);
  const [isLegacyChapter, setIsLegacyChapter] = useState(false);
  const [legacyReceivedAt, setLegacyReceivedAt] = useState('');
  const [legacyReceivedAtError, setLegacyReceivedAtError] = useState(false);
  const [donorType, setDonorType] = useState('individual');
  const [enterpriseName, setEnterpriseName] = useState('');
  const [enterpriseNameError, setEnterpriseNameError] = useState(false);

  const isOrganization = allowOrganizationDonor && donorType === 'organization';
  const memberLinkEnabled =
    allowMemberLink && !correctionsMode && !isOrganization;
  const addressFieldsRequired = !addressOptional;

  const clearMemberLinks = () => {
    setSelectedDonorId(null);
    setLinkedMemberLabel('');
    setSelectedLocalChapterDonorId(null);
    setLinkedLocalChapterLabel('');
    setSelectedSpdChapterDonorId(null);
    setLinkedSpdChapterLabel('');
  };

  const { showNotice, handleMutationError, noticeMessage } =
    useDonationAlreadyPaidHandler(onAlreadyPaid);

  const { mutate } = useApiSend(
    saveDonation,
    (data) => {
      setDonationId(data.id);
      refetch();
      handleNext();
    },
    (error) => {
      if (handleMutationError(error)) {
        return;
      }
      console.error(error);
    }
  );

  const { data: postalInfo } = useApiGet(
    ['postal-info', postalCode, countryId],
    getPostalInfo,
    {
      enabled: getPostalInfoActive,
    },
    { postalCode, countryId }
  );

  const { data: donorProfile } = useApiGet(
    ['donation-donor-personal-info', selectedDonorId],
    () => getUserPersonalInfo(selectedDonorId),
    { enabled: allowMemberLink && !!selectedDonorId, retry: 1 }
  );

  const { data: spdChapters = [] } = useApiGet(
    ['donation-pi-spd-chapters'],
    getSPDChaptersList,
    { enabled: allowMemberLink, retry: 1 }
  );

  const primaryDonorAddress = useMemo(() => {
    const addrs = donorProfile?.addresses;
    if (!addrs?.length) return undefined;
    return (
      addrs.find(
        (a) => a.address_type_id === PRIMARY_SHIPPING_ADDRESS_TYPE_ID
      ) || addrs[0]
    );
  }, [donorProfile]);

  useEffect(() => {
    if (donationObject) {
      setLoadedFromDonation(true);
      if (allowOrganizationDonor && donationObject.enterprise_name) {
        setDonorType('organization');
        setEnterpriseName(donationObject.enterprise_name);
        setFirstName('');
        setLastName('');
      } else {
        setDonorType('individual');
        setEnterpriseName('');
        setFirstName(donationObject.first_name || '');
        setLastName(donationObject.last_name || '');
      }
      setEmail(donationObject.email || '');
      setChapter(donationObject.chapter);
      setCountryId(donationObject.country_id);
      setStateId(donationObject.state_id);
      setCity(donationObject.city);
      setAddress(donationObject.address1);
      setAddress2(donationObject.address2);
      setPostalCode(donationObject.postal_code);
      setDelayPostalCode(donationObject.postal_code);
      setLocalChapter(donationObject.local_chapter_id);
      if (allowMemberLink) {
        setSelectedDonorId(donationObject.donor_id || null);
        if (donationObject.linked_donor) {
          const ld = donationObject.linked_donor;
          setLinkedMemberLabel(
            [ld.first_name, ld.last_name].filter(Boolean).join(' ') || ld.id
          );
        } else {
          setLinkedMemberLabel('');
        }
        setSelectedLocalChapterDonorId(
          donationObject.local_chapter_donor_id || null
        );
        setLinkedLocalChapterLabel(
          donationObject.linked_local_chapter?.name || ''
        );
        setSelectedSpdChapterDonorId(
          donationObject.spd_chapter_donor_id || null
        );
        setLinkedSpdChapterLabel(donationObject.linked_spd_chapter?.name || '');
        setIrsStatementExcluded(!!donationObject.irs_statement_excluded);
      }
      setIsLegacyChapter(!!donationObject.is_legacy_chapter);
      setLegacyReceivedAt(
        donationObject.legacy_received_at
          ? String(donationObject.legacy_received_at).slice(0, 10)
          : ''
      );
    }
  }, [donationObject, allowMemberLink, allowOrganizationDonor]);

  useEffect(() => {
    if (!allowMemberLink) return;
    if (selectedLocalChapterDonorId || selectedSpdChapterDonorId) {
      setIrsStatementExcluded(true);
    }
  }, [allowMemberLink, selectedLocalChapterDonorId, selectedSpdChapterDonorId]);

  const irsExcludedLocked = !!(
    selectedLocalChapterDonorId || selectedSpdChapterDonorId
  );

  useEffect(() => {
    if (!allowMemberLink || !donorProfile) return;
    setDonorType('individual');
    setEnterpriseName('');
    setLoadedFromDonation(true);
    if (donorProfile.first_name) setFirstName(donorProfile.first_name);
    if (donorProfile.last_name) setLastName(donorProfile.last_name);
    if (donorProfile.email) setEmail(donorProfile.email);
    if (donorProfile.local_chapter_id) {
      setLocalChapter(donorProfile.local_chapter_id);
    }
    if (primaryDonorAddress) {
      if (primaryDonorAddress.address_1) {
        setAddress(primaryDonorAddress.address_1);
      }
      if (primaryDonorAddress.address_2) {
        setAddress2(primaryDonorAddress.address_2);
      }
      if (primaryDonorAddress.city) setCity(primaryDonorAddress.city);
      if (primaryDonorAddress.postal_code) {
        setPostalCode(primaryDonorAddress.postal_code);
        setDelayPostalCode(primaryDonorAddress.postal_code);
      }
      if (primaryDonorAddress.country_id) {
        setCountryId(primaryDonorAddress.country_id);
      }
      if (primaryDonorAddress.state_id) {
        setStateId(primaryDonorAddress.state_id);
      }
    }
    const name = [donorProfile.first_name, donorProfile.last_name]
      .filter(Boolean)
      .join(' ');
    setLinkedMemberLabel(name || donorProfile.imis_id || selectedDonorId || '');
  }, [donorProfile, primaryDonorAddress, allowMemberLink, selectedDonorId]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPostalCode(delayPostalCode);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [delayPostalCode]);

  useEffect(() => {
    if (postalCode.length > 4 && countryId.length > 0 && !loadedFromDonation) {
      setGetPostalInfoActive(true);
    } else {
      setGetPostalInfoActive(false);
    }
  }, [postalCode, countryId, loadedFromDonation]);

  useEffect(() => {
    if (postalInfo) {
      if (postalInfo.city) {
        setCity(postalInfo.city);
      }
      if (postalInfo.state_id) {
        setStateId(postalInfo.state_id);
      }
    }
  }, [postalInfo]);

  const handleEmailChange = (valid, value) => {
    if (emailOptional && !value.trim()) {
      setEmailError(false);
      setEmail(value);
      return;
    }
    if (valid) {
      setEmailError(false);
    } else {
      setEmailError(true);
    }
    setEmail(value);
  };

  const optionalEmailValidator = (value) => {
    if (emailOptional && !String(value).trim()) return false;
    return emailValidator(value);
  };

  const handleEnterpriseNameChange = (event) => {
    const value = event.target.value;
    setEnterpriseName(value);
    if (value.length > 0) {
      setEnterpriseNameError(false);
    }
  };

  const handleDonorTypeChange = (_, value) => {
    if (!value) return;
    setDonorType(value);
    if (value === 'organization') {
      clearMemberLinks();
      setFirstNameError(false);
      setLastNameError(false);
    } else {
      setEnterpriseName('');
      setEnterpriseNameError(false);
    }
  };

  const handleFirstNameChange = (event) => {
    let value = event.target.value;
    setFirstName(value);
    if (value.length > 0) {
      setFirstNameError(false);
    }
  };

  const handleLastNameChange = (event) => {
    let value = event.target.value;
    setLastName(value);
    if (value.length > 0) {
      setLastNameError(false);
    }
  };

  const handleCountryChange = (value) => {
    setLoadedFromDonation(false);
    if (value) {
      if (value.id !== countryId) {
        setStateId('');
      }
      setCountryId(value.id);
      setStateRequired(value.has_states);
      setCountryIdError(false);
    } else {
      setCountryId('');
    }
  };

  const handleStateChange = (value) => {
    if (value) {
      setStateId(value.id);
      setStateIdError(false);
    } else {
      setStateId('');
    }
  };

  const handlePostalCodeChange = (event) => {
    setLoadedFromDonation(false);
    let value = event.target.value;
    setDelayPostalCode(value);
    if (value.length > 0) {
      setPostalCodeError(false);
    }
  };

  const handleAddressChange = (event) => {
    let value = event.target.value;
    setAddress(value);
    if (value.length > 0) {
      setAddressError(false);
    }
  };

  const handleCityChange = (event) => {
    let value = event.target.value;
    setCity(value);
    if (value.length > 0) {
      setCityError(false);
    }
  };

  const handleMovingNext = () => {
    if (isPaid && !correctionsMode) {
      onAlreadyPaid?.();
      return;
    }
    let valid = true;
    if (isOrganization) {
      if (enterpriseName.trim().length === 0) {
        setEnterpriseNameError(true);
        valid = false;
      }
    } else {
      if (firstName.length === 0) {
        setFirstNameError(true);
        valid = false;
      }
      if (lastName.length === 0) {
        setLastNameError(true);
        valid = false;
      }
    }
    if (!emailOptional && (email.length === 0 || emailError)) {
      setEmailError(true);
      valid = false;
    }
    if (emailOptional && email.trim().length > 0 && emailError) {
      setEmailError(true);
      valid = false;
    }
    if (addressFieldsRequired) {
      if (countryId.length === 0) {
        setCountryIdError(true);
        valid = false;
      }
      if (stateRequired && stateId.length === 0) {
        setStateIdError(true);
        valid = false;
      }
      if (city.length === 0) {
        setCityError(true);
        valid = false;
      }
      if (address.length === 0) {
        setAddressError(true);
        valid = false;
      }
      if (postalCode.length === 0) {
        setPostalCodeError(true);
        valid = false;
      }
    } else if (countryId.length > 0 && stateRequired && stateId.length === 0) {
      setStateIdError(true);
      valid = false;
    }
    const legacyOn =
      allowLegacyChapter && (legacyLocked || isLegacyChapter) && !!localChapter;
    if (legacyOn && !legacyReceivedAt) {
      setLegacyReceivedAtError(true);
      valid = false;
    }
    if (!valid) {
      console.log('Not valid');
      return;
    }
    let data = {
      first_name: isOrganization ? '' : trimField(firstName),
      last_name: isOrganization ? '' : trimField(lastName),
      enterprise_name: isOrganization ? trimField(enterpriseName) : '',
      email: trimField(email),
      chapter: chapter,
      country_id: countryId,
      state_id: stateId,
      city: city,
      address1: address,
      address2: address2,
      postal_code: postalCode,
      donation_id: donationId,
      local_chapter_id: localChapter,
    };
    if (allowMemberLink) {
      data.donor_id = selectedDonorId || null;
      data.local_chapter_donor_id = selectedLocalChapterDonorId || null;
      data.spd_chapter_donor_id = selectedSpdChapterDonorId || null;
      data.irs_statement_excluded = irsStatementExcluded;
    }
    if (allowLegacyChapter && !isPaid) {
      data.is_legacy_chapter = legacyOn;
      data.legacy_received_at = legacyOn ? legacyReceivedAt : null;
    }
    mutate(data);
  };

  const applyChapterDonorProfile = (profile) => {
    setDonorType('individual');
    setEnterpriseName('');
    setLoadedFromDonation(true);
    if (profile.first_name) setFirstName(profile.first_name);
    if (profile.last_name) setLastName(profile.last_name);
    if (profile.email) setEmail(profile.email);
    if (profile.address1) setAddress(profile.address1);
    if (profile.address2) setAddress2(profile.address2);
    if (profile.city) setCity(profile.city);
    if (profile.postal_code) {
      setPostalCode(profile.postal_code);
      setDelayPostalCode(profile.postal_code);
    }
    if (profile.country_id) setCountryId(profile.country_id);
    if (profile.state_id) setStateId(profile.state_id);
  };

  const handleClearMember = () => {
    setSelectedDonorId(null);
    setLinkedMemberLabel('');
  };

  const handleClearLocalChapterDonor = () => {
    setSelectedLocalChapterDonorId(null);
    setLinkedLocalChapterLabel('');
  };

  const handleClearSpdChapterDonor = () => {
    setSelectedSpdChapterDonorId(null);
    setLinkedSpdChapterLabel('');
  };

  const handleConfirmLocalChapterDonor = async () => {
    if (!pendingLocalChapter?.id) return;
    setChapterLinkError('');
    setChapterLinkLoading(true);
    try {
      const profile = await getChapterDonorProfile({
        local_chapter_id: pendingLocalChapter.id,
      });
      setSelectedLocalChapterDonorId(pendingLocalChapter.id);
      setSelectedSpdChapterDonorId(null);
      setSelectedDonorId(null);
      setLinkedLocalChapterLabel(
        profile.chapter_label || pendingLocalChapter.search_name || ''
      );
      setLinkedSpdChapterLabel('');
      setLinkedMemberLabel('');
      applyChapterDonorProfile(profile);
      setLocalChapter(pendingLocalChapter.id);
      setLocalChapterDialogOpen(false);
      setPendingLocalChapter(null);
    } catch (error) {
      setChapterLinkError(
        error?.error ||
          (typeof error === 'string' ? error : null) ||
          error?.message ||
          'Could not load chapter president'
      );
    } finally {
      setChapterLinkLoading(false);
    }
  };

  const handleConfirmSpdChapterDonor = async () => {
    if (!pendingSpdChapter?.id) return;
    setChapterLinkError('');
    setChapterLinkLoading(true);
    try {
      const profile = await getChapterDonorProfile({
        spd_chapter_id: pendingSpdChapter.id,
      });
      setSelectedSpdChapterDonorId(pendingSpdChapter.id);
      setSelectedLocalChapterDonorId(null);
      setSelectedDonorId(null);
      setLinkedSpdChapterLabel(
        profile.chapter_label || pendingSpdChapter.name || ''
      );
      setLinkedLocalChapterLabel('');
      setLinkedMemberLabel('');
      applyChapterDonorProfile(profile);
      setSpdChapterDialogOpen(false);
      setPendingSpdChapter(null);
    } catch (error) {
      setChapterLinkError(
        error?.error ||
          (typeof error === 'string' ? error : null) ||
          error?.message ||
          'Could not load chapter president'
      );
    } finally {
      setChapterLinkLoading(false);
    }
  };

  const handleSelectUser = (user) => {
    setDonorType('individual');
    setEnterpriseName('');
    setSelectedDonorId(user.id);
    setSelectedLocalChapterDonorId(null);
    setSelectedSpdChapterDonorId(null);
    setLinkedLocalChapterLabel('');
    setLinkedSpdChapterLabel('');
    const label =
      user.full_name ||
      [user.first_name, user.last_name].filter(Boolean).join(' ') ||
      user.imis_id ||
      user.id;
    setLinkedMemberLabel(label);
    setUserSearchModalOpen(false);
  };

  return (
    <Grid container>
      {showNotice && (
        <Grid item xs={12} padding>
          <Alert severity="info" role="status">
            {noticeMessage}
          </Alert>
        </Grid>
      )}
      <Grid item xs={12} paddingTop={5} />
      {allowOrganizationDonor && (
        <Grid item xs={12} paddingBottom={2}>
          <Typography variant="subtitle2" gutterBottom>
            Donor type
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={donorType}
            onChange={handleDonorTypeChange}
          >
            <ToggleButton value="individual">Individual</ToggleButton>
            <ToggleButton value="organization">Organization</ToggleButton>
          </ToggleButtonGroup>
        </Grid>
      )}
      {memberLinkEnabled && (
        <Grid item xs={12} paddingBottom={2}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
          >
            <Button
              variant="outlined"
              size="small"
              onClick={() => setUserSearchModalOpen(true)}
            >
              Search member
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setChapterLinkError('');
                setPendingLocalChapter(null);
                setLocalChapterDialogOpen(true);
              }}
            >
              Select local chapter
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setChapterLinkError('');
                setPendingSpdChapter(null);
                setSpdChapterDialogOpen(true);
              }}
            >
              Select S/P/D chapter
            </Button>
            {selectedDonorId && (
              <Chip
                label={linkedMemberLabel || selectedDonorId}
                onDelete={handleClearMember}
                size="small"
              />
            )}
            {selectedLocalChapterDonorId && (
              <Chip
                label={linkedLocalChapterLabel || selectedLocalChapterDonorId}
                onDelete={handleClearLocalChapterDonor}
                size="small"
              />
            )}
            {selectedSpdChapterDonorId && (
              <Chip
                label={linkedSpdChapterLabel || selectedSpdChapterDonorId}
                onDelete={handleClearSpdChapterDonor}
                size="small"
              />
            )}
            {(selectedDonorId ||
              selectedLocalChapterDonorId ||
              selectedSpdChapterDonorId) && (
              <Typography variant="caption" color="text.secondary">
                Address fields below can be edited after linking a donor source.
              </Typography>
            )}
          </Stack>
          {chapterLinkError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {chapterLinkError}
            </Alert>
          )}
        </Grid>
      )}
      {isOrganization ? (
        <Grid item xs={12} paddingBottom={2}>
          <TextField
            variant="outlined"
            required
            fullWidth
            id="organization-name"
            label="Organization name"
            error={enterpriseNameError}
            helperText={
              enterpriseNameError ? 'Organization name is required' : ''
            }
            autoFocus
            value={enterpriseName}
            onChange={handleEnterpriseNameChange}
          />
        </Grid>
      ) : (
        <>
          <Grid item xs={6} paddingRight={1} paddingBottom={2}>
            <TextField
              variant="outlined"
              required
              fullWidth
              id="first-name"
              label="First Name"
              error={firstNameError}
              helperText={firstNameError ? 'First Name is required' : ''}
              autoFocus
              value={firstName}
              onChange={handleFirstNameChange}
            />
          </Grid>
          <Grid item xs={6} paddingLeft={1} paddingBottom={2}>
            <TextField
              variant="outlined"
              required
              fullWidth
              id="last-name"
              label="Last Name"
              value={lastName}
              error={lastNameError}
              helperText={lastNameError ? 'Last Name is required' : ''}
              onChange={handleLastNameChange}
            />
          </Grid>
        </>
      )}
      <Grid item xs={6} paddingRight={1} paddingBottom={2}>
        <ValidatedTextField
          label={emailOptional ? 'Email (optional)' : 'Email'}
          size="medium"
          validator={optionalEmailValidator}
          onChange={(valid, value) => handleEmailChange(valid, value)}
          errorOverride={
            emailError
              ? email.trim()
                ? 'Invalid email address'
                : 'Email is required'
              : ''
          }
          valueRequired={!emailOptional}
          value={email}
        />
      </Grid>
      <Grid item xs={6} paddingLeft={1} paddingBottom={2}>
        <DonationsChapterSelector
          chapter={localChapter}
          handleChapterSelect={(value) => setLocalChapter(value)}
          label={'P.E.O. Chapter'}
        />
      </Grid>
      {allowLegacyChapter && localChapter && (
        <Grid item xs={12} paddingBottom={1}>
          <FormControlLabel
            control={
              <Checkbox
                checked={legacyLocked || isLegacyChapter}
                disabled={legacyLocked || isPaid}
                onChange={(e) => {
                  setIsLegacyChapter(e.target.checked);
                  if (!e.target.checked) {
                    setLegacyReceivedAt('');
                    setLegacyReceivedAtError(false);
                  }
                }}
              />
            }
            label="Special Recognition"
          />
          {(legacyLocked || isLegacyChapter) && (
            <TextField
              type="date"
              label="Received at"
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={legacyReceivedAt}
              disabled={isPaid}
              error={legacyReceivedAtError}
              helperText={
                legacyReceivedAtError ? 'Received at is required' : ''
              }
              onChange={(e) => {
                setLegacyReceivedAt(e.target.value);
                setLegacyReceivedAtError(false);
              }}
              sx={{ mt: 1, maxWidth: 280 }}
            />
          )}
        </Grid>
      )}
      <Grid item xs={12} padding>
        <Divider />
      </Grid>
      {addressOptional && (
        <Grid item xs={12} paddingBottom={1}>
          <Typography variant="body2" color="text.secondary">
            Mailing address optional — email-only donations are allowed.
          </Typography>
        </Grid>
      )}
      <Grid item xs={12} paddingTop={2} />
      <Grid item xs={6} paddingRight={1} paddingBottom={2}>
        <CountrySelector
          value={countryId}
          handleChange={handleCountryChange}
          error={addressFieldsRequired && countryIdError}
          errorMessage={'Country is required'}
          size="medium"
        />
      </Grid>
      <Grid item xs={6} paddingLeft={1} paddingBottom={2}>
        <TextField
          variant="outlined"
          required={addressFieldsRequired}
          fullWidth
          id="past-code"
          label="Postal Code"
          value={delayPostalCode}
          error={addressFieldsRequired && postalCodeError}
          helperText={
            addressFieldsRequired && postalCodeError
              ? 'Postal Code is required'
              : ''
          }
          onChange={handlePostalCodeChange}
        />
      </Grid>
      <Grid item xs={6} paddingRight={1} paddingBottom={2}>
        <TextField
          variant="outlined"
          required={addressFieldsRequired}
          fullWidth
          id="address-1"
          label="Address 1"
          value={address}
          error={addressFieldsRequired && addressError}
          helperText={
            addressFieldsRequired && addressError ? 'Address is required' : ''
          }
          onChange={handleAddressChange}
        />
      </Grid>
      <Grid item xs={6} paddingLeft={1} paddingBottom={2}>
        <TextField
          variant="outlined"
          fullWidth
          id="address-2"
          label="Address 2"
          value={address2}
          onChange={(e) => setAddress2(e.target.value)}
        />
      </Grid>
      <Grid item xs={6} paddingRight={1} paddingBottom={2}>
        <TextField
          variant="outlined"
          required={addressFieldsRequired}
          fullWidth
          id="city"
          label="City"
          value={city}
          error={addressFieldsRequired && cityError}
          helperText={
            addressFieldsRequired && cityError ? 'City is required' : ''
          }
          onChange={handleCityChange}
        />
      </Grid>
      <Grid item xs={6} paddingLeft={1} paddingBottom={2}>
        <StateSelector
          value={stateId}
          countryId={countryId}
          handleChange={handleStateChange}
          error={stateIdError}
          errorMessage={'State is required'}
          size="medium"
        />
      </Grid>
      {allowMemberLink && !correctionsMode && !isPaid && (
        <Grid item xs={12} paddingBottom={1}>
          <FormControlLabel
            control={
              <Checkbox
                checked={irsStatementExcluded}
                disabled={irsExcludedLocked}
                onChange={(e) => setIrsStatementExcluded(e.target.checked)}
              />
            }
            label="Exclude IRS tax statement on donor acknowledgement letter"
          />
        </Grid>
      )}
      {isPaid && !correctionsMode && (
        <Grid item xs={12} padding>
          <Alert severity="info">
            This donation has already been completed and can no longer be
            edited.
          </Alert>
        </Grid>
      )}
      <Grid item xs={12} padding textAlign={'center'}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleMovingNext()}
          disabled={isPaid && !correctionsMode}
          sx={{ fontWeight: 'bold' }}
        >
          {'Next'}
        </Button>
      </Grid>
      {memberLinkEnabled && (
        <>
          <UserSearchModal
            modalOpen={userSearchModalOpen}
            setModalOpen={setUserSearchModalOpen}
            userObjectRet
            setUser={handleSelectUser}
            activeMembers
            statusFilter="active"
            allowSetToMe={false}
          />
          <Dialog
            open={localChapterDialogOpen}
            onClose={() =>
              !chapterLinkLoading && setLocalChapterDialogOpen(false)
            }
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Select local chapter</DialogTitle>
            <DialogContent>
              <ChapterSelector
                chapter={pendingLocalChapter?.id || ''}
                spd={null}
                all
                keyId="donation-pi-local-chapter-donor"
                label="Local chapter"
                objectReturned
                handleChapterSelect={(value) => setPendingLocalChapter(value)}
              />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setLocalChapterDialogOpen(false)}
                disabled={chapterLinkLoading}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirmLocalChapterDonor}
                disabled={!pendingLocalChapter?.id || chapterLinkLoading}
                startIcon={
                  chapterLinkLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : null
                }
              >
                Use chapter president
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={spdChapterDialogOpen}
            onClose={() =>
              !chapterLinkLoading && setSpdChapterDialogOpen(false)
            }
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Select S/P/D chapter</DialogTitle>
            <DialogContent>
              <Autocomplete
                options={spdChapters}
                getOptionLabel={(o) => o.name ?? ''}
                value={pendingSpdChapter}
                onChange={(_, nv) => setPendingSpdChapter(nv)}
                renderInput={(params) => (
                  <TextField {...params} label="S/P/D chapter" />
                )}
              />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setSpdChapterDialogOpen(false)}
                disabled={chapterLinkLoading}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirmSpdChapterDonor}
                disabled={!pendingSpdChapter?.id || chapterLinkLoading}
                startIcon={
                  chapterLinkLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : null
                }
              >
                Use chapter president
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Grid>
  );
}
